import React, { useEffect, useState } from 'react';
import { PlusIcon, BoxesIcon, CheckCircle2Icon, XCircleIcon, Loader2Icon, StarIcon, CpuIcon, Trash2Icon, PencilIcon, ZapIcon } from 'lucide-react';
import { useScreenInit } from '../useScreenInit';
import { useModelStore, modelUsageLabel } from '../stores/modelStore';
import { useAgentStore } from '../stores/agentStore';
import { useUiStore } from '../stores/uiStore';
import { ModelConfig, ModelProvider, SdkType, ModelCapability } from '../types';
import { PageContainer, PageHeader } from '../components/common/page';
import { Card, Button, Badge, Input, Select, Label } from '../components/common/ui';
import { Modal } from '../components/common/Modal';
import { formatRelative } from '../utils/format';
const CAPABILITIES: ModelCapability[] = ['tool_use', 'vision', 'long_context', 'json_mode'];
export function ModelCenterPage() {
  useScreenInit();
  const models = useModelStore(s => s.models);
  const testing = useModelStore(s => s.testing);
  const testResults = useModelStore(s => s.testResults);
  const {
    addModel,
    updateModel,
    deleteModel,
    setDefault,
    setClaudeCodeDefault,
    testConnection
  } = useModelStore();
  const agents = useAgentStore(s => s.agents);
  const pushToast = useUiStore(s => s.pushToast);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ModelConfig | null>(null);
  const usedModelIds = new Set(agents.map(a => a.defaultModelId));
  const handleDelete = (m: ModelConfig) => {
    if (usedModelIds.has(m.modelId)) {
      pushToast('error', `模型「${m.name}」正被 Agent 引用，无法删除 (409 冲突)`);
      return;
    }
    if (confirm(`确认删除模型「${m.name}」？`)) {
      deleteModel(m.modelId);
      pushToast('success', '模型已删除');
    }
  };
  const handleTest = async (m: ModelConfig) => {
    await testConnection(m.modelId);
    const r = useModelStore.getState().testResults[m.modelId];
    pushToast(r?.success ? 'success' : 'error', r?.message || '测试完成');
  };
  return <PageContainer data-id="element-724">
      <PageHeader title="模型中心" description="配置多个大模型，并选择 Claude Code SDK 当前执行模型" action={<Button onClick={() => {
      setEditing(null);
      setModalOpen(true);
    }} data-id="element-726">
            <PlusIcon className="h-4 w-4" data-id="element-727" /> 新增模型
          </Button>} data-id="element-725" />

      <div className="space-y-3" data-id="element-728">
        {models.map(m => {
        const usage = modelUsageLabel(m);
        const result = testResults[m.modelId];
        const isTesting = testing[m.modelId];
        return <Card key={m.modelId} className="p-4" data-id="element-729">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center" data-id="element-730">
                <div className="flex min-w-0 flex-1 items-start gap-3" data-id="element-731">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-800" data-id="element-732">
                    <BoxesIcon className="h-5 w-5 text-blue-400" data-id="element-733" />
                  </div>
                  <div className="min-w-0" data-id="element-734">
                    <div className="flex flex-wrap items-center gap-2" data-id="element-735">
                      <span className="font-medium text-slate-100" data-id="element-736">
                        {m.name}
                      </span>
                      <Badge tone="slate" data-id="element-737">{m.provider}</Badge>
                      {m.enabled ? result && !result.success ? <Badge tone="red" data-id="element-738">连接失败</Badge> : <Badge tone="green" data-id="element-739">启用</Badge> : <Badge tone="slate" data-id="element-740">禁用</Badge>}
                    </div>
                    <div className="mt-0.5 font-mono text-xs text-slate-500" data-id="element-741">
                      {m.modelName}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5" data-id="element-742">
                      {usage.map(u => <Badge key={u} tone={u.includes('Claude') ? 'indigo' : 'amber'} data-id="element-743">
                          {u}
                        </Badge>)}
                      {m.capabilities.map(c => <Badge key={c} tone="slate" data-id="element-744">
                          {c}
                        </Badge>)}
                    </div>
                    {result && <div className="mt-2 flex items-center gap-1.5 text-xs" data-id="element-745">
                        {result.success ? <CheckCircle2Icon className="h-3.5 w-3.5 text-emerald-400" data-id="element-746" /> : <XCircleIcon className="h-3.5 w-3.5 text-red-400" data-id="element-747" />}
                        <span className={result.success ? 'text-emerald-300' : 'text-red-300'} data-id="element-748">
                          {result.message} · {result.latencyMs}ms
                        </span>
                      </div>}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 lg:flex-col lg:items-end" data-id="element-749">
                  <span className="hidden text-xs text-slate-500 lg:block" data-id="element-750">
                    更新于 {formatRelative(m.updatedAt)}
                  </span>
                  <div className="flex flex-wrap gap-1.5" data-id="element-751">
                    <Button variant="outline" size="sm" onClick={() => handleTest(m)} disabled={isTesting} data-id="element-752">
                      {isTesting ? <Loader2Icon className="h-3.5 w-3.5 animate-spin" data-id="element-753" /> : <ZapIcon className="h-3.5 w-3.5" data-id="element-754" />}
                      测试连接
                    </Button>
                    {!m.isDefault && <Button variant="ghost" size="sm" onClick={() => {
                  setDefault(m.modelId);
                  pushToast('success', '已设为默认模型');
                }} data-id="element-755">
                        <StarIcon className="h-3.5 w-3.5" data-id="element-756" /> 设为默认
                      </Button>}
                    {!m.isClaudeCodeDefault && <Button variant="ghost" size="sm" onClick={() => {
                  setClaudeCodeDefault(m.modelId);
                  pushToast('success', 'Claude Code SDK 执行模型已更新');
                }} data-id="element-757">
                        <CpuIcon className="h-3.5 w-3.5" data-id="element-758" /> Claude Code 模型
                      </Button>}
                    <Button variant="ghost" size="sm" onClick={() => {
                  setEditing(m);
                  setModalOpen(true);
                }} aria-label="编辑" data-id="element-759">
                      <PencilIcon className="h-3.5 w-3.5" data-id="element-760" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(m)} aria-label="删除" className="text-red-400 hover:text-red-300" data-id="element-761">
                      <Trash2Icon className="h-3.5 w-3.5" data-id="element-762" />
                    </Button>
                  </div>
                </div>
              </div>
            </Card>;
      })}
      </div>

      <ModelFormModal open={modalOpen} editing={editing} onClose={() => setModalOpen(false)} onSubmit={data => {
      if (editing) {
        updateModel(editing.modelId, {
          name: data.name,
          provider: data.provider,
          sdkType: data.sdkType,
          modelName: data.modelName,
          baseUrl: data.baseUrl,
          enabled: data.enabled,
          capabilities: data.capabilities,
          defaultParams: data.defaultParams
        });
        pushToast('success', '模型已更新');
      } else {
        addModel({
          name: data.name,
          provider: data.provider,
          sdkType: data.sdkType,
          modelName: data.modelName,
          baseUrl: data.baseUrl,
          apiKey: data.apiKey,
          enabled: data.enabled,
          capabilities: data.capabilities,
          defaultParams: data.defaultParams
        });
        pushToast('success', '模型已添加');
      }
      setModalOpen(false);
    }} data-id="element-763" />
    </PageContainer>;
}
interface FormData {
  name: string;
  provider: ModelProvider;
  sdkType: SdkType;
  modelName: string;
  baseUrl?: string;
  apiKey: string;
  enabled: boolean;
  capabilities: ModelCapability[];
  defaultParams: {
    temperature?: number;
    maxTokens?: number;
    topP?: number;
  };
}
function ModelFormModal({
  open,
  editing,
  onClose,
  onSubmit
}: {
  open: boolean;
  editing: ModelConfig | null;
  onClose: () => void;
  onSubmit: (data: FormData) => void;
}) {
  const [data, setData] = useState<FormData>(initial(editing));
  useEffect(() => {
    if (open) setData(initial(editing));
  }, [open, editing]);
  const set = (patch: Partial<FormData>) => setData(d => ({
    ...d,
    ...patch
  }));
  const valid = data.name.trim() && data.modelName.trim() && (editing || data.apiKey.trim());
  return <Modal open={open} onClose={onClose} title={editing ? '编辑模型' : '新增模型'} size="lg" footer={<>
          <Button variant="outline" onClick={onClose} data-id="element-765">
            取消
          </Button>
          <Button onClick={() => onSubmit(data)} disabled={!valid} data-id="element-766">
            {editing ? '保存' : '创建'}
          </Button>
        </>} data-id="element-764">
      <div className="space-y-4" data-id="element-767">
        <div data-id="element-768">
          <Label required data-id="element-769">模型名称</Label>
          <Input value={data.name} onChange={e => set({
          name: e.target.value
        })} placeholder="例如 Claude Sonnet 4" data-id="element-770" />
        </div>
        <div className="grid grid-cols-2 gap-3" data-id="element-771">
          <div data-id="element-772">
            <Label data-id="element-773">Provider</Label>
            <Select value={data.provider} onChange={e => set({
            provider: e.target.value as ModelProvider
          })} data-id="element-774">
              <option value="anthropic" data-id="element-775">anthropic</option>
              <option value="openai_compatible" data-id="element-776">openai_compatible</option>
              <option value="custom" data-id="element-777">custom</option>
            </Select>
          </div>
          <div data-id="element-778">
            <Label data-id="element-779">SDK 类型</Label>
            <Select value={data.sdkType} onChange={e => set({
            sdkType: e.target.value as SdkType
          })} data-id="element-780">
              <option value="claude_code_sdk" data-id="element-781">claude_code_sdk</option>
              <option value="generic_llm" data-id="element-782">generic_llm</option>
              <option value="openai_compatible" data-id="element-783">openai_compatible</option>
            </Select>
          </div>
        </div>
        <div data-id="element-784">
          <Label required data-id="element-785">Model ID</Label>
          <Input value={data.modelName} onChange={e => set({
          modelName: e.target.value
        })} placeholder="claude-sonnet-4-20250514" className="font-mono" data-id="element-786" />
        </div>
        <div data-id="element-787">
          <Label data-id="element-788">Base URL</Label>
          <Input value={data.baseUrl || ''} onChange={e => set({
          baseUrl: e.target.value
        })} placeholder="https://api.openai.com/v1 (可选)" className="font-mono" data-id="element-789" />
        </div>
        <div data-id="element-790">
          <Label required={!editing} data-id="element-791">API Key</Label>
          <Input type="password" value={data.apiKey} onChange={e => set({
          apiKey: e.target.value
        })} placeholder={editing ? '保留为空则不修改' : 'sk-...'} className="font-mono" data-id="element-792" />
        </div>
        <div data-id="element-793">
          <Label data-id="element-794">默认参数</Label>
          <div className="grid grid-cols-3 gap-3" data-id="element-795">
            <Input type="number" step="0.1" value={data.defaultParams.temperature ?? ''} onChange={e => set({
            defaultParams: {
              ...data.defaultParams,
              temperature: Number(e.target.value)
            }
          })} placeholder="temperature" data-id="element-796" />
            <Input type="number" value={data.defaultParams.maxTokens ?? ''} onChange={e => set({
            defaultParams: {
              ...data.defaultParams,
              maxTokens: Number(e.target.value)
            }
          })} placeholder="max_tokens" data-id="element-797" />
            <Input type="number" step="0.1" value={data.defaultParams.topP ?? ''} onChange={e => set({
            defaultParams: {
              ...data.defaultParams,
              topP: Number(e.target.value)
            }
          })} placeholder="top_p" data-id="element-798" />
          </div>
        </div>
        <div data-id="element-799">
          <Label data-id="element-800">能力标签</Label>
          <div className="flex flex-wrap gap-2" data-id="element-801">
            {CAPABILITIES.map(c => {
            const active = data.capabilities.includes(c);
            return <button key={c} type="button" onClick={() => set({
              capabilities: active ? data.capabilities.filter(x => x !== c) : [...data.capabilities, c]
            })} className={`rounded-md border px-2.5 py-1 text-xs transition-colors ${active ? 'border-indigo-500/40 bg-indigo-500/15 text-indigo-300' : 'border-slate-700 text-slate-400 hover:bg-slate-800'}`} data-id="element-802">
                  {c}
                </button>;
          })}
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-300" data-id="element-803">
          <input type="checkbox" checked={data.enabled} onChange={e => set({
          enabled: e.target.checked
        })} className="h-4 w-4 rounded border-slate-600 bg-slate-800 text-indigo-500" data-id="element-804" />
          启用此模型
        </label>
      </div>
    </Modal>;
}
function initial(editing: ModelConfig | null): FormData {
  return {
    name: editing?.name || '',
    provider: editing?.provider || 'anthropic',
    sdkType: editing?.sdkType || 'claude_code_sdk',
    modelName: editing?.modelName || '',
    baseUrl: editing?.baseUrl || '',
    apiKey: '',
    enabled: editing?.enabled ?? true,
    capabilities: editing?.capabilities || ['tool_use'],
    defaultParams: editing?.defaultParams || {
      temperature: 0.2,
      maxTokens: 8192
    }
  };
}